/**
 * [C28] New component (no old counterpart; the old app showed a blank screen — V-57): shown by the portal's
 * error boundary when a screen crashes (D-259). The error is already reported with the screen's trace id;
 * the short reference lets staff find it in the admin console. Visual language of the program's cards.
 */
import { AlertTriangle, RotateCcw } from "lucide-react";
import { useT } from "../i18n-context.js";

export interface CrashNoticeProps {
  readonly traceRef: string;
  readonly onRetry: () => void;
}

export function CrashNotice({ traceRef, onRetry }: CrashNoticeProps) {
  const t = useT();
  return (
    <div data-testid="crash-notice" role="alert" className="mx-auto mt-10 max-w-lg rounded-3xl border border-red-100 bg-white p-8 text-center shadow-sm dark:border-red-900/40 dark:bg-[#161b22]">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500 dark:bg-red-900/20">
        <AlertTriangle size={28} />
      </div>
      <h2 className="mb-2 text-lg font-black text-gray-800 dark:text-[#e6edf3]">{t("app.crash_title")}</h2>
      <p className="mb-4 text-sm font-bold leading-relaxed text-gray-500 dark:text-[#8b949e]">{t("app.crash_text")}</p>
      <p className="mb-6 text-xs font-bold text-gray-400 dark:text-[#8b949e]">
        {t("error.trace_ref", { id: "" })}
        <span dir="ltr" className="font-mono text-gray-600 dark:text-[#c9d1d9]">
          {traceRef}
        </span>
      </p>
      <button type="button" onClick={onRetry} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-black text-white shadow-lg shadow-blue-500/20 transition hover:bg-blue-700">
        <RotateCcw size={16} />
        {t("app.crash_retry")}
      </button>
    </div>
  );
}
